import { CheckCircle2, ChevronLeft, ChevronRight, ClipboardList, Plus, CalendarDays, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusDot } from '@/components/ui/status-dot';
import { useAthleteTrainingCalendar } from '@/hooks/useAthleteTrainingCalendar';
import { supabase } from '@/lib/customSupabaseClient';
import SessionBuilder from '@/pages/SessionBuilder';

function assignmentName(session) {
  return session.sessions?.name || 'Unnamed Session';
}

function assignedStatus(session) {
  if (session.workout_status === 'completed') return { label: 'Completed', variant: 'success' };
  if (session.workout_status === 'in_progress') return { label: 'In Progress', variant: 'default' };
  if (session.workout_status === 'abandoned') return { label: 'Abandoned', variant: 'warning' };
  return { label: 'Not Started', variant: 'secondary' };
}

function DayCell({ day, onSelect, onAssign }) {
  if (!day.date || day.dayNumber == null) {
    return <div className="aspect-square min-w-0" />;
  }

  return (
    <div
      className={`relative flex aspect-square min-w-0 flex-col rounded-md border text-sm backdrop-blur-sm ${
        day.isSelected
          ? 'border-primary bg-primary/25'
          : day.isToday
            ? 'border-primary bg-background/80'
            : 'border-border/70 bg-background/70'
      }`}
    >
      <button
        type="button"
        aria-label={`${day.date}, ${day.assignedCount} assigned, ${day.programCount} from program, ${day.completedCount} completed`}
        aria-pressed={day.isSelected}
        className="flex min-h-0 flex-1 flex-col items-center justify-center rounded-md hover:bg-background/20"
        onClick={() => onSelect(day.date)}
      >
        <span className={day.assignedCount > 0 || day.programCount > 0 ? 'font-semibold text-primary' : 'font-medium text-foreground'}>
          {day.dayNumber}
        </span>
        <span className="mt-0.5 flex h-1.5 items-center gap-0.5">
          {day.completedCount > 0 && <StatusDot status="success" className="h-1.5 w-1.5" />}
          {day.assignedCount > 0 && <StatusDot status="info" className="h-1.5 w-1.5" />}
          {day.programCount > 0 && <StatusDot status="program" className="h-1.5 w-1.5" />}
        </span>
      </button>
      {day.isSelected && (
        <button
          type="button"
          aria-label={`Assign a session on ${day.date}`}
          className="absolute bottom-0.5 right-0.5 flex h-5 w-5 items-center justify-center rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={() => onAssign(day.date)}
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

function CompletedRow({ activity, userId }) {
  return (
    <Link
      to={`/user-activity/${userId}/session/${activity.id}`}
      className="flex items-center gap-3 rounded-md border bg-muted/30 p-3 hover:bg-muted/50"
    >
      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{activity.name}</p>
        {activity.durationMinutes != null && (
          <p className="text-xs text-muted-foreground">{activity.durationMinutes} min</p>
        )}
      </div>
    </Link>
  );
}

function AssignedRow({ session, onUnassign }) {
  const status = assignedStatus(session);
  return (
    <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-3">
      <ClipboardList className="h-4 w-4 shrink-0 text-blue-500" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{assignmentName(session)}</p>
        {session.due_date && (
          <p className="text-xs text-muted-foreground">
            Due {new Date(`${session.due_date}T00:00:00`).toLocaleDateString()}
          </p>
        )}
      </div>
      <Badge variant={status.variant} className="text-xs">
        {status.label}
      </Badge>
      {onUnassign && session.is_active !== false && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
          aria-label={`Remove ${assignmentName(session)} from this athlete's schedule`}
          onClick={() => onUnassign(session)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function ProgramRow({ session }) {
  return (
    <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-3">
      <CalendarDays className="h-4 w-4 shrink-0 text-lime-600" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{session.name || 'Unnamed Session'}</p>
        <p className="text-xs text-muted-foreground">
          {[session.programName, session.durationMinutes != null ? `${session.durationMinutes} min` : null]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
      <Badge variant="outline" className="text-xs">
        Program
      </Badge>
    </div>
  );
}

export function AthleteTrainingCalendar({
  userId,
  assignedSessions = [],
  clientEmail = '',
  onAssigned,
  onUnassigned,
}) {
  const [builderOpen, setBuilderOpen] = useState(false);
  const {
    days,
    monthLabel,
    weekdayLabels,
    selectedDate,
    selectedCompleted,
    selectedAssigned,
    selectedProgramSessions,
    undatedSessions,
    upcomingPrograms,
    unstartedPrograms,
    isLoading,
    goToPreviousMonth,
    goToNextMonth,
    selectDate,
  } = useAthleteTrainingCalendar({ userId, assignedSessions });

  const handleUnassign = async (session) => {
    const name = assignmentName(session);
    if (!window.confirm(`Remove "${name}" from this athlete's schedule? The assignment is kept as history.`)) {
      return;
    }
    const { error } = await supabase
      .from('client_assigned_sessions')
      .update({ is_active: false })
      .eq('id', session.id);
    if (error) {
      toast.error('Could not remove assignment', { description: error.message });
      return;
    }
    toast.success(`Removed "${name}" from the schedule`);
    onUnassigned?.();
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          Training calendar
        </CardTitle>
        <CardDescription>
          Completed training in green. Coach assignments in blue. Activated program in lime.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Previous month"
            onClick={goToPreviousMonth}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <p className="text-sm font-medium">{monthLabel}</p>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Next month"
            onClick={goToNextMonth}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-7 gap-1">
          {weekdayLabels.map((label, index) => (
            <div key={`weekday-${index}`} className="text-center text-xs text-muted-foreground">
              {label}
            </div>
          ))}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 35 }, (_, index) => (
              <Skeleton key={index} className="aspect-square rounded-md" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, index) => (
              <DayCell
                key={day.date ?? `padding-${index}`}
                day={day}
                onAssign={(date) => {
                  selectDate(date);
                  setBuilderOpen(true);
                }}
                onSelect={selectDate}
              />
            ))}
          </div>
        )}

        {selectedCompleted.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Completed
            </p>
            {selectedCompleted.map((activity) => (
              <CompletedRow activity={activity} key={activity.id} userId={userId} />
            ))}
          </div>
        )}

        {selectedAssigned.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Assigned
            </p>
            {selectedAssigned.map((session) => (
              <AssignedRow key={session.id} onUnassign={handleUnassign} session={session} />
            ))}
          </div>
        )}

        {selectedProgramSessions.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              From program
            </p>
            {selectedProgramSessions.map((session) => (
              <ProgramRow key={session.id} session={session} />
            ))}
          </div>
        )}

        {upcomingPrograms.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Upcoming programs
            </p>
            {upcomingPrograms.map((program) => (
              <p key={program.programId} className="text-sm text-muted-foreground">
                {program.name} starts {program.startLabel}.
              </p>
            ))}
          </div>
        )}

        {unstartedPrograms.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Elapsed programs
            </p>
            {unstartedPrograms.map((program) => (
              <p key={program.programId} className="text-sm text-muted-foreground">
                {program.name} has no upcoming sessions on the calendar.
              </p>
            ))}
          </div>
        )}

        {selectedCompleted.length === 0 &&
          selectedAssigned.length === 0 &&
          selectedProgramSessions.length === 0 &&
          upcomingPrograms.length === 0 &&
          unstartedPrograms.length === 0 &&
          !isLoading && (
          <EmptyState
            className="py-6"
            description="No training completed, nothing assigned, and no program session for this day."
            title="Nothing on this day"
          />
        )}

        {undatedSessions.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Without a date
            </p>
            {undatedSessions.map((session) => (
              <AssignedRow key={session.id} onUnassign={handleUnassign} session={session} />
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={builderOpen} onOpenChange={setBuilderOpen}>
        <DialogContent
          className="max-h-[90vh] w-[95vw] max-w-5xl overflow-y-auto"
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Assign session</DialogTitle>
            <DialogDescription>
              {selectedDate
                ? `Session Builder for ${selectedDate}. Assign or create a session for this athlete.`
                : 'Session Builder for this athlete.'}
            </DialogDescription>
          </DialogHeader>
          {builderOpen && (
            <SessionBuilder
              embedded
              initialClientEmail={clientEmail}
              initialDueDate={selectedDate || ''}
              initialMode="assign"
              lockClient={Boolean(clientEmail)}
              onAssigned={() => {
                setBuilderOpen(false);
                onAssigned?.();
              }}
              onCancel={() => setBuilderOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
